import * as React from 'react';
import { useRef, useState, useEffect } from 'react';
import Sticky from 'react-sticky-el';
import { JumpList, SvgImage, Overlay } from '../common';
import FlatFilterList from '../FlatFilterList';
import SearchField from '../common/SearchField';
import breakpoints from '../../breakpoints';
import FiltersPanelProps from './type';
import mapFilterType from '../../mutators';
import { getUrlParamValue } from '../../utilities/browser';
import './styles.scss';

interface JumpListOption {
  displayText: string;
  value: string;
  selectedValue?: boolean;
  name?: string;
}

interface SelectedValues {
  length: number;
  selected: any;
}

const FiltersPanel = ({
  hasSearch,
  visibleFilters,
  additionalFilters,
  visibleFilterCallback,
  additionalFilterCallback,
  searchLabel,
  searchPlaceholder,
  additionalFiltersApplyButtonLabel,
  additionalFiltersLabel,
  searchFieldCallback,
  searchTerm,
  disableSticky = false,
  disableFirst = false,
  themeColour = 'default',
  allowMultipleSelection = false,
  defaultDatePickerValue,
  onDateChange,
  includeDates,
  datePickerLocale,
  onSearchClear,
}: FiltersPanelProps) => {
  const filters = useRef<HTMLDivElement>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isOverlayActive, setIsOverlayActive] = useState<boolean>(false);
  const [selectedValues, setSelectedValues] = useState<SelectedValues>();
  const [selectedAdditionalValues, setSelectedAdditionalValues] = useState<SelectedValues>();

  /**
   * For now we dont want the sticky nav to be show
   * on mobile view or small tablet.
   */
  const handleIsMobile = () => {
    const screenWidth = window.outerWidth;
    setIsMobile(screenWidth <= breakpoints.md);
  };

  /**
   * Probably could of simplified this tbh.
   * Essentialy adding and removing selected
   * filters from the visible filters and the
   * additional filters.
   * @param event
   * @param additional
   */
  const handleSelection = (event: JumpListOption) => {
    const entry: any = {};
    entry[`${event.name}`] = event.value;
    const selected = Object.assign(
      selectedValues?.selected || {},
      entry,
    );
    const newSelection: SelectedValues = {
      length: Object.keys(selected).length,
      selected,
    };

    setSelectedValues(newSelection);
  };

  const handleMultipleSelection = (events: JumpListOption[]) => {
    const entry = events.reduce((acc, event) => {
      if (event?.name) {
        if (!acc[event.name]) {
          acc[event.name] = event.value;
        } else {
          acc[event.name] = `${acc[event.name]},${event.value}`;
        }
      }

      return acc;
    }, {} as any);

    const selected = Object.assign(
      selectedAdditionalValues?.selected || {},
      entry,
    );

    const newSelection: SelectedValues = {
      length: Object.keys(selected).length,
      selected,
    };

    setSelectedAdditionalValues(newSelection);
  };

  const checkForQueryParamValue = (key: string, additional = false) => {
    const queryStringParam = getUrlParamValue(key);

    if (!additional) {
      return queryStringParam || selectedValues?.selected[key];
    }
    return queryStringParam || selectedAdditionalValues?.selected[key];
  };

  /**
   * Triggering the visibleFilterCallback whenever
   * selected values are updated.
   */
  useEffect(() => {
    if (visibleFilterCallback && selectedValues) {
      visibleFilterCallback(selectedValues);
    }
  }, [selectedValues]);

  /**
   * Add resize event listener to handle isMobile state
   */
  useEffect(() => {
    handleIsMobile();
    window.addEventListener('resize', () => handleIsMobile());
    return () => {
      window.removeEventListener('resize', () => handleIsMobile);
    };
  }, []);

  /**
   * Disable scroll on html element
   */
  useEffect(() => {
    if (isOverlayActive) {
      document.documentElement.classList.add('noscroll');
    } else {
      document.documentElement.classList.remove('noscroll');
    }
  }, [isOverlayActive]);

  /**
   * Check for current selections in the additional filters items.
   */
  useEffect(() => {
    if (additionalFilters) {
      const selected: any = {};
      additionalFilters.map((item) => {
        const querystringValue = checkForQueryParamValue(item.queryParam);
        if (querystringValue) {
          selected[item.queryParam] = querystringValue;
        }
        return false;
      });

      setSelectedAdditionalValues({
        length: selected.length,
        selected,
      });
    }
  }, [additionalFilters]);

  return (
    <>
      <Sticky
        stickyClassName="container filters-panel--is-sticky"
        topOffset={0}
        disabled={isMobile || disableSticky}
      >
        <div className={`filters-panel ${themeColour}`} ref={filters}>
          <div className="filters-panel__inner">
            {hasSearch && (
              <div className="filters-panel__filter">
                <div className="filters-panel__search">
                  <SearchField
                    name="search"
                    placeholder={searchPlaceholder}
                    label={searchLabel || ''}
                    callback={searchFieldCallback}
                    value={searchTerm || ''}
                    onSearchClear={onSearchClear}
                  />
                </div>
              </div>
            )}

            {visibleFilters && visibleFilters.map((filterItem, index) => (
              <div className="filters-panel__filter" key={filterItem.label}>
                <JumpList
                  name={filterItem.queryParam}
                  theme="filter-style blue"
                  icon="icon-plus"
                  items={mapFilterType(filterItem.items)}
                  action={(event: JumpListOption) => handleSelection(event)}
                  selectedValue={checkForQueryParamValue(filterItem.queryParam)}
                  useScroller
                  useSearch={hasSearch}
                  disableFirst={disableFirst && !index}
                  withDatePicker={filterItem.withDatePicker}
                  defaultDatePickerValue={defaultDatePickerValue}
                  onDateChange={onDateChange}
                  includeDates={includeDates}
                  datePickerLocale={datePickerLocale}
                />
              </div>
            ))}

            {additionalFilters && (
              <div className="filters-panel__filter filters-panel__filter--view-more">
                <button onClick={() => setIsOverlayActive(!isOverlayActive)}>
                  <span>{additionalFiltersLabel}</span>
                  <SvgImage reference="#icon-plus" title="view more filters" />
                </button>
              </div>
            )}
          </div>
        </div>
      </Sticky>

      {isOverlayActive && (
        <Overlay handleClose={() => setIsOverlayActive(false)}>
          <div className="filters-overlay">
            {additionalFilters && additionalFiltersLabel && (
              <>
                <div className="filters-overlay__content">
                  {additionalFilters?.map((additionalFilter) => (
                    <>
                      {Boolean(additionalFilter.items.length) && (
                        <FlatFilterList
                          key={additionalFilter.label}
                          name={additionalFilter.label}
                          queryParam={additionalFilter.queryParam}
                          items={mapFilterType(additionalFilter.items)}
                          viewMoreLabel={additionalFiltersLabel}
                          action={(events: JumpListOption[]) => handleMultipleSelection(events)}
                          selectedValue={
                            selectedAdditionalValues?.selected[additionalFilter?.queryParam]
                            || additionalFilter.items[0].code
                          }
                          allowMultipleSelection={allowMultipleSelection}
                        />
                      )}
                    </>

                  ))}
                </div>
                <div className="filters-overlay__apply">
                  <button onClick={
                    () => {
                      if (additionalFilterCallback) {
                        additionalFilterCallback(selectedAdditionalValues);
                        setIsOverlayActive(false);
                        document?.body.classList.remove('overlay-is-showing');
                      }
                    }
                  }
                  >
                    {additionalFiltersApplyButtonLabel}
                  </button>
                </div>
              </>
            )}
          </div>
        </Overlay>
      )}
    </>
  );
};

export default FiltersPanel;
